import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import type { Easing } from "framer-motion";
import { AnimatedSection, TextReveal } from "./animations";
import { useInsights, useFeaturedInsight } from "@/hooks/useInsights";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";

// Shared animation config
const easeStandard: Easing = [0.25, 0.4, 0.25, 1];

// Default placeholder images for insights without cover images
const placeholderImages = [
  "https://images.unsplash.com/photo-1518837695005-2083093ee35b?w=1200&q=90",
  "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80",
  "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=800&q=80",
  "https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=800&q=80",
  "https://images.unsplash.com/photo-1509316785289-025f5b846b35?w=800&q=80",
];

const Clients = () => {
  const { data: allInsights, isLoading } = useInsights();
  const { data: featuredInsight, isLoading: featuredLoading } = useFeaturedInsight();

  if (isLoading || featuredLoading) {
    return (
      <section className="border-b border-border">
        {/* Header Skeleton */}
        <div className="border-b border-border">
          <div className="px-6 md:px-12 py-16 md:py-24">
            <Skeleton className="h-32 w-3/4" />
          </div>
        </div>
        {/* Featured Skeleton */}
        <Skeleton className="aspect-[21/9] w-full" />
        {/* Grid Skeleton */}
        <div className="grid grid-cols-2 md:grid-cols-4">
          {[0, 1, 2, 3].map((index) => (
            <div key={index} className="p-6 md:p-8 border-r border-border">
              <Skeleton className="h-4 w-20 mb-4" />
              <Skeleton className="aspect-[4/3] w-full mb-4" />
              <Skeleton className="h-4 w-full" />
            </div>
          ))}
        </div>
      </section>
    );
  }

  const insights = allInsights || [];

  // Use featured insight from DB, or fall back to first insight
  const heroInsight = featuredInsight || insights[0];
  const otherInsights = heroInsight
    ? insights.filter((i) => i.id !== heroInsight.id).slice(0, 4)
    : insights.slice(1, 5);

  // If no insights at all, show empty state
  if (!heroInsight && insights.length === 0) {
    return (
      <section className="border-b border-border">
        <div className="border-b border-border overflow-visible">
          <div className="px-6 md:px-12 py-16 md:py-24 flex flex-col md:flex-row items-start md:items-end justify-between gap-8">
            <div className="flex items-baseline gap-2">
              <TextReveal delay={0.2}>
                <h2 className="font-display text-[clamp(4rem,18vw,14rem)] leading-[0.85] uppercase tracking-tighter text-foreground pr-4 overflow-visible">
                  THINKING
                </h2>
              </TextReveal>
              <motion.span
                className="font-mono text-sm md:text-base text-accent font-medium"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5, duration: 0.5 }}
              >
                .03
              </motion.span>
            </div>
          </div>
        </div>
        <div className="px-6 md:px-12 py-16 text-center">
          <p className="text-muted-foreground">No insights published yet.</p>
        </div>
      </section>
    );
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    try {
      return format(new Date(dateStr), "MMMM d, yyyy");
    } catch {
      return "";
    }
  };

  const getImage = (insight: typeof heroInsight, index: number = 0) => {
    return insight?.cover_image_url || placeholderImages[index % placeholderImages.length];
  };

  return (
    <section className="border-b border-border">
      {/* THINKING Header */}
      <div className="border-b border-border overflow-visible">
        <div className="px-6 md:px-12 py-16 md:py-24 flex flex-col md:flex-row items-start md:items-end justify-between gap-8">
          {/* Title */}
          <div className="flex items-baseline gap-2">
            <TextReveal delay={0.2}>
              <h2 className="font-display text-[clamp(4rem,18vw,14rem)] leading-[0.85] uppercase tracking-tighter text-foreground pr-4 overflow-visible">
                THINKING
              </h2>
            </TextReveal>
            <motion.span
              className="font-mono text-sm md:text-base text-accent font-medium"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5, duration: 0.5 }}
            >
              .03
            </motion.span>
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="text-sm md:text-base text-muted-foreground leading-relaxed max-w-md"
          >
            Notes on product direction, design leadership and what actually
            works when AI moves into creative and product operations.
          </motion.p>

          {/* Count */}
          <AnimatedSection delay={0.4} direction="up">
            <motion.span
              className="font-display text-[clamp(4rem,15vw,12rem)] leading-[0.85] tracking-tighter text-foreground/20 inline-block cursor-default"
              whileHover={{ rotate: 360, color: "hsl(var(--foreground))" }}
              transition={{ duration: 0.8, ease: "easeInOut" }}
            >
              ∞
            </motion.span>
          </AnimatedSection>
        </div>
      </div>

      {/* Featured Insight */}
      {heroInsight && (
        <Link
          to={`/thinking/${heroInsight.slug}`}
          className="group block relative aspect-[21/9] overflow-hidden border-b border-border"
        >
          <motion.img
            src={getImage(heroInsight, 0)}
            alt={heroInsight.title}
            className="w-full h-full object-cover"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.6, ease: easeStandard }}
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

          {/* Date */}
          <span className="absolute top-5 left-5 text-white/80 text-xs bg-black/30 backdrop-blur-sm px-3 py-1 rounded-full">
            {formatDate(heroInsight.published_at)}
          </span>

          {/* Featured badge */}
          {heroInsight.is_featured && (
            <motion.span
              className="absolute top-5 right-5 bg-accent text-white px-4 py-1.5 text-[10px] uppercase tracking-wider font-medium rounded-full"
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5, type: "spring" }}
            >
              FEATURED
            </motion.span>
          )}

          {/* Title */}
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
            <motion.h3
              className="font-display text-2xl md:text-4xl uppercase text-white tracking-tight max-w-2xl"
              initial={{ y: 20, opacity: 0.8 }}
              whileHover={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.35 }}
            >
              {heroInsight.title.toUpperCase()}
            </motion.h3>
            <motion.div
              className="h-0.5 bg-accent mt-4 rounded-full max-w-xs"
              initial={{ scaleX: 0, originX: 0 }}
              whileHover={{ scaleX: 1 }}
              transition={{ duration: 0.4 }}
            />
          </div>
        </Link>
      )}

      {/* Other Insights Grid */}
      {otherInsights.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4">
          {otherInsights.map((insight, index) => (
            <motion.div
              key={insight.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <Link
                to={`/thinking/${insight.slug}`}
                className={`group block p-6 md:p-8 border-b md:border-b-0 ${
                  index < otherInsights.length - 1 ? "border-r border-border" : ""
                } hover:bg-muted transition-colors duration-medium`}
              >
                <span className="text-xs text-muted-foreground block mb-4">
                  {formatDate(insight.published_at)}
                </span>
                <div className="aspect-[4/3] bg-muted mb-4 overflow-hidden rounded-lg">
                  <motion.img
                    src={getImage(insight, index + 1)}
                    alt={insight.title}
                    className="w-full h-full object-cover"
                    whileHover={{ scale: 1.08 }}
                    transition={{ duration: 0.5, ease: easeStandard }}
                    loading="lazy"
                  />
                </div>
                <h4 className="font-display text-sm md:text-base uppercase tracking-tight line-clamp-2 group-hover:text-accent transition-colors duration-medium">
                  {insight.title.toUpperCase()}
                </h4>
              </Link>
            </motion.div>
          ))}
        </div>
      )}
    </section>
  );
};

export default Clients;
